import React, { createContext, useState, useEffect, ReactNode, useContext, useRef } from 'react';
import { User } from 'oidc-client-ts';
import { AuthenticationState } from '../monad/AuthenticationState';
import axios, { AxiosInstance } from 'axios';
import { AuthContextType, useAuthContext, userManager } from './AuthContext';
import i18n from '../i18n/i18n';

export interface HttpClientContextType {
  anonymousHttpClient: AxiosInstance;
  authenticatedHttpClient: AxiosInstance;
  authenticatedClientReady: boolean;
}

interface HttpClientProviderProps {
  children: ReactNode;
}

export const HttpClientContext = createContext<HttpClientContextType | undefined>(undefined);

export const useHttpClientContext = (): HttpClientContextType => {
  const context = useContext<HttpClientContextType | undefined>(HttpClientContext);
  if (!context) {
    throw new Error("authContext is undefined, need to be inside AuthProvider.");
  }
  return context;
}

export const HttpClienthProvider: React.FC<HttpClientProviderProps> = ({ children }) => {
  const authContext = useAuthContext();
  const didSetupInterceptors = useRef(false);
  const [authenticatedClientReady, setAuthenticatedClientReady] = useState(false);

  useEffect(() => {
    console.log("HttpClientContext useEffect()", authContext.user);
    console.log("didSetupInterceptors", didSetupInterceptors);
    authContext.user.match({
      loggedIn: (_) => {
        if (!didSetupInterceptors.current) {
          console.log("setting up interceptors");
          setupAuthenticatedAxiosInterceptors(authContext);
          didSetupInterceptors.current = true;
          setAuthenticatedClientReady(true);
        }
      }
    })
  }, [authContext.user]);

  return (
    <HttpClientContext.Provider value={{ anonymousHttpClient, authenticatedHttpClient, authenticatedClientReady }}>
      {children}
    </HttpClientContext.Provider>
  );
};

const authenticatedHttpClient = axios.create({
  baseURL: 'https://api.porsche.nu/api:porscheklubben-festival',
  headers: { 'Content-Type': 'application/json' },
});

const anonymousHttpClient = axios.create({
  baseURL: 'https://api.porsche.nu/api:porscheklubben-festival',
  headers: { 'Content-Type': 'application/json' },
});

// Add request interceptor to set 'lang' header for all requests
const languageInterceptor = (config: any) => {
  let hasLangHeader = false;
  if (config.headers) {
    if (typeof config.headers.has === 'function') {
      hasLangHeader = config.headers.has('lang');
    } else {
      hasLangHeader = Object.keys(config.headers).some(key => key.toLowerCase() === 'lang');
    }
  }

  if (!hasLangHeader) {
    const language = (i18n as any).language || 'sv';
    const langCode = language.split('-')[0]; // Extract 'sv' from 'sv-SE'
    if (config.headers && typeof config.headers.set === 'function') {
      config.headers.set('lang', langCode);
    } else {
      config.headers['lang'] = langCode;
    }
  }
  return config;
};

authenticatedHttpClient.interceptors.request.use(languageInterceptor);
anonymousHttpClient.interceptors.request.use(languageInterceptor);

var refreshTokenPromise: Promise<User | null> = Promise.resolve(null); // Shared promise for token refresh
var isRefreshing = false;

export const setupAuthenticatedAxiosInterceptors = (authContext: AuthContextType) => {

  authenticatedHttpClient.interceptors.request.use(async (config) => {

    /*const user = authContext.user.getOrNull();
    console.log("Set authorization header", user);*/
    const user = await userManager.getUser();
    console.log("setupAuthenticatedAxiosInterceptors user", user);

    if (user) {
      config.headers.Authorization = `Bearer ${user.access_token}`;
    }
    return config;
  },
    (error) => Promise.reject(error)
  );

  authenticatedHttpClient.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        console.log("Try refresh token");
        // If a refresh request is already in progress, wait for it
        if (!isRefreshing) {
          refreshTokenPromise = authContext.refreshToken();
        }

        try {
          const newUser = await refreshTokenPromise;

          console.log("Refresh token response", newUser);
          isRefreshing = false; // Reset after success

          if (!newUser) {
            return Promise.reject("User was null after token refresh.");
          }

          //authContext.setUser(AuthenticationState.loggedIn(newUser));
          // Update the original request with the new token
          originalRequest.headers.Authorization = `Bearer ${newUser.access_token}`;

          console.log("Original request doing retry", originalRequest);

          return axios(originalRequest);
        } catch (refreshError) {
          isRefreshing = false; // Reset on failure
          //authContext.logout();
          authContext.setUser(AuthenticationState.didExpire());
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
};



export interface ApiEvent {
  eventId: string;
  name: string;
  heading: string;
  startDate: string;
  startTime: string;
  endTime: string;
  description: string;
  location?: string;
  preBookingReq: string;
  visibleParticipantList: string;
  allowedToCheckIn: string;
  isPersonal: string;
  checkInPossible: string;
  mainEventId?: string;
}

export interface ApiParticipant {
  bookingInfo: string;
  checkedIn: string;
  id: number;
  membershipNumber: string;
  name: string;
  userKey: string;
  visibleForOthers: string;
  partnerMembershipNumber?: string;
}

export interface ApiBooking {
  eventId: string;
  userKey: string;
  name: string;
  bookingInfo: string;
  partnerName: string;
  partnerBookingInfo: string;
  //visibleForOthers: string;
  booked: string;
  partnerBooked: string;
  checkedIn: string;
  checkInPossible: string;
}

export interface ApiBannerMessage {
  id?: number;
  title: string;
  message: string;
  type: string;
  priority: number;
}